import type { ReactNode } from "react";
import type { Audit, AuditReport } from "@/services/api/guardiansv2/types";
import type { ScopeLabelCache } from "../audits/useScopeLabelCache";
import { EntityRichText } from "../shared/EntityRichText";
import { entityTextScopeFromAuditScope } from "../shared/entityTags";

export interface AuditReportPanelProps {
  audit: Audit;
  /** True while synthesis is still running. */
  running?: boolean;
  /** Optional "View full context" action for the unit of work. */
  unitOfWorkAction?: ReactNode;
  scopeLabels?: ScopeLabelCache;
}

/**
 * Report-first view of an audit: where the issue was, how it happened, the
 * context it happened in, and recommended actions. Falls back to the legacy
 * ``executive_summary`` for audits created before structured reports existed.
 */
export function AuditReportPanel({
  audit,
  running,
  unitOfWorkAction,
  scopeLabels,
}: AuditReportPanelProps) {
  const entityScope = entityTextScopeFromAuditScope(audit.scope);

  if (running) {
    return (
      <div
        className="rounded-lg border border-indigo-500/25 bg-indigo-500/10 px-4 py-6 text-center"
        data-testid="audit-report-running"
      >
        <p className="text-sm font-medium text-indigo-200">Building report…</p>
        <p className="mt-1 text-xs text-indigo-300/80">
          Aggregating Guardian verdicts into an issue-first report.
        </p>
      </div>
    );
  }

  const report = audit.report ?? null;
  if (!report) {
    return (
      <div data-testid="audit-report-fallback">
        <div className="text-sm leading-relaxed text-slate-300">
          <EntityRichText
            text={audit.executive_summary || "No report available yet."}
            scope={entityScope}
            scopeLabels={scopeLabels}
          />
        </div>
        {unitOfWorkAction ? <div className="mt-4">{unitOfWorkAction}</div> : null}
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="audit-report">
      {report.headline ? (
        <div className="text-base font-semibold leading-snug text-slate-100">
          <EntityRichText text={report.headline} scope={entityScope} scopeLabels={scopeLabels} />
        </div>
      ) : null}
      <ReportSection title="Where the issue was" body={report.where} entityScope={entityScope} scopeLabels={scopeLabels} />
      <ReportSection title="How it happened" body={report.what_happened} entityScope={entityScope} scopeLabels={scopeLabels} />
      <ReportSection title="Context" body={report.context} entityScope={entityScope} scopeLabels={scopeLabels} />
      <RecommendedActions actions={report.recommended_actions} entityScope={entityScope} scopeLabels={scopeLabels} />
      {unitOfWorkAction ? <div className="pt-1">{unitOfWorkAction}</div> : null}
    </div>
  );
}

function ReportSection({
  title,
  body,
  entityScope,
  scopeLabels,
}: {
  title: string;
  body: string;
  entityScope: ReturnType<typeof entityTextScopeFromAuditScope>;
  scopeLabels?: ScopeLabelCache;
}) {
  if (!body?.trim()) return null;
  return (
    <section>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">{title}</h3>
      <div className="mt-1.5 text-sm leading-relaxed text-slate-300">
        <EntityRichText text={body} scope={entityScope} scopeLabels={scopeLabels} />
      </div>
    </section>
  );
}

function RecommendedActions({
  actions,
  entityScope,
  scopeLabels,
}: {
  actions: AuditReport["recommended_actions"];
  entityScope: ReturnType<typeof entityTextScopeFromAuditScope>;
  scopeLabels?: ScopeLabelCache;
}) {
  if (!actions || actions.length === 0) return null;
  return (
    <section>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
        Recommended actions
      </h3>
      <ul className="mt-1.5 list-disc space-y-1 pl-5 text-sm leading-relaxed text-slate-300">
        {actions.map((action, idx) => (
          <li key={idx}>
            <EntityRichText text={action} scope={entityScope} scopeLabels={scopeLabels} />
          </li>
        ))}
      </ul>
    </section>
  );
}
